import React from 'react';
import { CheckCircle2, Loader2, LocateFixed, MapPinned, Trash2 } from 'lucide-react';
import DeliveryZoneMapEditor from '../../../components/maps/DeliveryZoneMapEditor';
import { formatPrice } from '../../../lib/mockData';
import type { DeliveryZone } from '../../../types';

type DeliverySettingsSectionProps = {
  form: {
    deliveryEnabled: boolean;
    pickupEnabled: boolean;
    deliveryFixedFee: string;
    deliveryMapCenterLat: string;
    deliveryMapCenterLng: string;
  };
  deliveryZones: DeliveryZone[];
  deviceLocationLoading: boolean;
  addressLocationLoading: boolean;
  deviceLocationError: string | null;
  deliveryZoneFeedback: { type: 'success' | 'error'; message: string } | null;
  updateToggleField: (field: 'deliveryEnabled' | 'pickupEnabled', value: boolean) => void;
  updateDeliveryZone: (zoneId: string, updates: Partial<DeliveryZone>) => void;
  useDeviceLocationForDeliveryMap: () => void;
  centerDeliveryMapFromRestaurantAddress: () => void | Promise<void>;
  setDeliveryZones: React.Dispatch<React.SetStateAction<DeliveryZone[]>>;
  setDeliveryZoneFeedback: React.Dispatch<React.SetStateAction<{ type: 'success' | 'error'; message: string } | null>>;
};

const DeliverySettingsSection: React.FC<DeliverySettingsSectionProps> = ({
  form,
  deliveryZones,
  deviceLocationLoading,
  addressLocationLoading,
  deviceLocationError,
  deliveryZoneFeedback,
  updateToggleField,
  updateDeliveryZone,
  useDeviceLocationForDeliveryMap,
  centerDeliveryMapFromRestaurantAddress,
  setDeliveryZones,
  setDeliveryZoneFeedback,
}) => {
  const activeZonesCount = deliveryZones.filter((zone) => zone.isActive).length;
  const pausedZonesCount = deliveryZones.filter((zone) => !zone.isActive).length;

  return (
    <div id="secao-entrega" className="card scroll-mt-24 space-y-6 p-5 2xl:p-6">
      <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-end 2xl:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
            <MapPinned className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Areas de entrega</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Configure onde o restaurante entrega. O cliente so consegue pedir delivery quando o endereco cai dentro de uma area ativa.
            </p>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-3 text-sm sm:grid-cols-3 2xl:min-w-[520px]">
          <div>
            <p className="text-xs text-muted-foreground">Areas ativas</p>
            <p className="mt-1 text-xl font-black text-white">{activeZonesCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Areas pausadas</p>
            <p className="mt-1 text-xl font-black text-white">{pausedZonesCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Taxa padrao</p>
            <p className="mt-1 text-xl font-black text-primary">{formatPrice(Number(form.deliveryFixedFee) || 0)}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {[
          ['1', 'Centralize o mapa', 'Use o endereco do restaurante ou a localizacao atual para abrir a regiao correta.'],
          ['2', 'Desenhe a cobertura', 'Clique em Nova area no Google Maps e marque os limites onde voce entrega.'],
          ['3', 'Defina taxa e salve', 'Informe a taxa de cada area, ative ou pause regioes e salve as configuracoes.'],
        ].map(([step, title, description]) => (
          <div key={step} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-black text-white">{step}</div>
            <p className="font-semibold text-white">{title}</p>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">{description}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
          <p className="text-sm font-semibold text-white">Como o cliente pode comprar?</p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <button
              type="button"
              onClick={() => updateToggleField('deliveryEnabled', !form.deliveryEnabled)}
              className={`rounded-2xl border px-4 py-4 text-left transition-all ${form.deliveryEnabled ? 'border-primary bg-primary/12 text-white' : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'}`}
            >
              <p className="font-semibold">{form.deliveryEnabled ? 'Delivery ativo' : 'Delivery pausado'}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {form.deliveryEnabled ? 'Pedidos de entrega estao liberados dentro das areas ativas.' : 'Novos pedidos de entrega estao bloqueados.'}
              </p>
            </button>
            <button
              type="button"
              onClick={() => updateToggleField('pickupEnabled', !form.pickupEnabled)}
              className={`rounded-2xl border px-4 py-4 text-left transition-all ${form.pickupEnabled ? 'border-primary bg-primary/12 text-white' : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'}`}
            >
              <p className="font-semibold">{form.pickupEnabled ? 'Retirada ativa' : 'Retirada desativada'}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {form.pickupEnabled ? 'Cliente fora da area pode retirar no local.' : 'Cliente fora da area nao tera retirada como alternativa.'}
              </p>
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
          <div>
            <p className="text-sm font-semibold text-white">Ponto inicial do mapa</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Atual: {Number(form.deliveryMapCenterLat).toFixed(5)}, {Number(form.deliveryMapCenterLng).toFixed(5)}. Isso apenas centraliza o mapa; a cobertura real e desenhada abaixo.
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => void centerDeliveryMapFromRestaurantAddress()}
              disabled={addressLocationLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#2A2A2A] bg-[#101010] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {addressLocationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPinned className="h-4 w-4" />}
              {addressLocationLoading ? 'Localizando...' : 'Usar endereco do restaurante'}
            </button>
            <button
              type="button"
              onClick={useDeviceLocationForDeliveryMap}
              disabled={deviceLocationLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#2A2A2A] bg-[#101010] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deviceLocationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
              {deviceLocationLoading ? 'Buscando...' : 'Usar minha localizacao'}
            </button>
          </div>
        </div>
      </div>

      {deviceLocationError && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {deviceLocationError}
        </div>
      )}

      <div className="grid gap-6 2xl:grid-cols-[minmax(760px,1.6fr)_minmax(460px,0.9fr)]">
        <DeliveryZoneMapEditor
          zones={deliveryZones}
          center={{
            lat: Number(form.deliveryMapCenterLat) || -19.9167,
            lng: Number(form.deliveryMapCenterLng) || -43.9345,
          }}
          onZonesChange={setDeliveryZones}
        />

        <div className="space-y-3 2xl:max-h-[760px] 2xl:overflow-y-auto 2xl:pr-1">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <h3 className="text-sm font-semibold text-white">Areas cadastradas</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Nomeie cada area e defina a taxa. Arraste os pontos no mapa para ajustar a borda da cobertura.
            </p>
          </div>

          {deliveryZones.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-5 text-sm text-muted-foreground">
              Nenhuma area desenhada ainda. Clique em Nova area no mapa e marque pelo menos 3 pontos.
            </div>
          ) : (
            <div className="grid gap-3 xl:grid-cols-2 2xl:grid-cols-1">
              {deliveryZones.map((zone, index) => (
                <div key={zone.id} className="space-y-3 rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wide text-muted-foreground">Nome da area</label>
                    <input
                      value={zone.name ?? ''}
                      onChange={(event) => updateDeliveryZone(zone.id, { name: event.target.value })}
                      className="input"
                      placeholder={`Ex.: Centro, Zona Sul, Bairro ${index + 1}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wide text-muted-foreground">Taxa desta area</label>
                    <div className="flex items-center gap-2 rounded-xl border border-[#2A2A2A] bg-[#151515] px-3">
                      <span className="text-sm text-muted-foreground">R$</span>
                      <input
                        value={String(zone.deliveryFee ?? 0)}
                        onChange={(event) => updateDeliveryZone(zone.id, { deliveryFee: Math.max(0, Number(event.target.value) || 0) })}
                        className="w-full bg-transparent py-3 text-sm text-white outline-none"
                        inputMode="decimal"
                        placeholder="0,00"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Essa taxa substitui a taxa fixa geral quando o cliente cai dentro desta regiao.
                    </p>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{zone.isActive ? 'Area ativa' : 'Area pausada'}</p>
                      <p className="text-xs text-muted-foreground">
                        {zone.isActive ? 'Essa area vale para novos pedidos de delivery.' : 'Essa area fica salva, mas nao entra na validacao.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDeliveryZone(zone.id, { isActive: !zone.isActive })}
                      className={`rounded-xl border px-3 py-2 text-xs font-semibold ${zone.isActive ? 'border-primary bg-primary/12 text-white' : 'border-[#2A2A2A] bg-[#1A1A1A] text-muted-foreground'}`}
                    >
                      {zone.isActive ? 'Ativa' : 'Pausada'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDeliveryZones((current) => current.filter((item) => item.id !== zone.id));
                      setDeliveryZoneFeedback(null);
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/20 px-3 py-3 text-sm font-semibold text-red-300 transition hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir area
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {deliveryZoneFeedback && (
        <div
          className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${
            deliveryZoneFeedback.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
              : 'border-red-500/30 bg-red-500/10 text-red-200'
          }`}
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4" />
          <span>{deliveryZoneFeedback.message}</span>
        </div>
      )}

      <div className="rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A]/60 p-4 text-sm text-muted-foreground">
        A validacao oficial da area roda no backend usando Turf. Mesmo que o cliente tente manipular o frontend, um pedido de delivery so e confirmado se o ponto estiver dentro de uma area ativa.
      </div>
    </div>
  );
};

export default DeliverySettingsSection;
